import { Injectable, NgZone, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { firstValueFrom } from 'rxjs';
import { PartnerService } from '../partner.service';

const RETRY_DELAY_MS = 300;
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i;

@Injectable({ providedIn: 'root' })
export class NotificationLinkService {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly partnerService = inject(PartnerService);

  async open(link: string | undefined): Promise<void> {
    if (!link) {
      return;
    }
    if (!ABSOLUTE_URL_PATTERN.test(link)) {
      this.navigateInApp(link.startsWith('/') ? link : `/${link}`);
      return;
    }
    const url = new URL(link);
    const businessAppOrigin = await this.getBusinessAppOrigin();
    if (businessAppOrigin !== null && url.origin !== businessAppOrigin) {
      await this.openInBrowser(link);
      return;
    }
    this.navigateInApp(url.pathname + url.search);
  }

  private async openInBrowser(link: string): Promise<void> {
    try {
      await Browser.open({ url: link, presentationStyle: 'popover' });
    } catch (error) {
      console.error('Failed to open notification link in the browser', error);
    }
  }

  private getBusinessAppOrigin(): Promise<string | null> {
    return firstValueFrom(this.partnerService.currentBusinessAppOrigin$).catch((error) => {
      console.error('Failed to look up the Business App origin for a notification link', error);
      return null;
    });
  }

  private navigateInApp(url: string): void {
    const urlTree = this.router.parseUrl(url);
    this.zone.run(() => this.navigateWithRetry(urlTree));
  }

  private async navigateWithRetry(urlTree: UrlTree): Promise<void> {
    const success = await this.router.navigateByUrl(urlTree);
    if (!success) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      this.router.navigateByUrl(urlTree);
    }
  }
}
